import {
  Sparkles,
  Eraser,
  Scissors,
  Brush,
  Maximize2,
  ArrowUpFromLine,
  Replace,
  Palette,
  Layers,
  Repeat,
  PenTool,
  Compass,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DIALOG_SHEET_CLASS } from '../lib/modal-classes';
import type { AICapability } from '../types';

interface AIToolPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired when the user picks a capability. The dialog closes itself
   *  via `onOpenChange(false)` immediately before this fires, so the
   *  consumer just needs to update its `selectedAITool` state. */
  onSelect: (capability: AICapability) => void;
}

interface ToolCard {
  capability: AICapability;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Provider-agnostic capability list. Each maps to one sub-panel inside
// the AI section of PropertiesPanel; provider/model choices live inside
// the sub-panel, not on the picker card.
//
// Ordering is by what-the-user-is-trying-to-do, grouped roughly:
//   1. create something new
//   2. modify / fill a selected region
//   3. modify the whole image (text-driven or img2img)
//   4. background ops
//   5. quality (upscale)
//   6. specialty (vector / guided generation)
const TOOLS: ToolCard[] = [
  {
    capability: 'smart-create',
    label: 'Smart Create',
    description: 'Generate a new image from a text prompt and drop it on a new layer.',
    icon: Sparkles,
  },
  {
    capability: 'smart-delete',
    label: 'Smart Delete',
    description: 'Remove the marquee selection and fill from the surrounding pixels.',
    icon: Eraser,
  },
  {
    capability: 'inpaint',
    label: 'Inpaint',
    description: 'Replace the masked region with content matching a prompt.',
    icon: Brush,
  },
  {
    capability: 'search-replace',
    label: 'Search & Replace',
    description: 'Replace an object described by text with something else, no manual mask.',
    icon: Replace,
  },
  {
    capability: 'search-recolor',
    label: 'Search & Recolor',
    description: 'Recolor an object described by text without changing its shape.',
    icon: Palette,
  },
  {
    capability: 'outpaint',
    label: 'Outpaint',
    description: 'Extend the canvas in any direction with AI-generated content.',
    icon: Maximize2,
  },
  {
    capability: 'image-to-image',
    label: 'Image to Image',
    description: 'Reimagine the whole image with a prompt and an adjustable strength.',
    icon: Repeat,
  },
  {
    capability: 'remove-background',
    label: 'Remove Background',
    description: 'Strip the background from the active image, leaving the subject.',
    icon: Scissors,
  },
  {
    capability: 'replace-background',
    label: 'Replace Background',
    description: 'Composite the subject onto a new prompt-described background.',
    icon: Layers,
  },
  {
    capability: 'upscale',
    label: 'Upscale',
    description: 'Increase image resolution — choose crisp or creative reimagining.',
    icon: ArrowUpFromLine,
  },
  {
    capability: 'vectorize',
    label: 'Vectorize',
    description: 'Convert a raster image into a scalable SVG vector.',
    icon: PenTool,
  },
  {
    capability: 'control',
    label: 'Control / Guided',
    description: 'Guide generation from a sketch, structure, or reference style.',
    icon: Compass,
  },
];

export function AIToolPickerDialog({ open, onOpenChange, onSelect }: AIToolPickerDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${DIALOG_SHEET_CLASS} sm:max-w-2xl`}>
        <DialogHeader>
          <DialogTitle>AI Tools</DialogTitle>
        </DialogHeader>
        {/* Scrollable grid — capped at ~70vh so on short screens the user
            can still scroll the list without the dialog itself overflowing
            the viewport. min-h-0 lets the inner overflow take effect when
            the dialog is in flex layout. */}
        <div className="overflow-y-auto max-h-[70vh] min-h-0 pr-1 -mr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TOOLS.map((tool) => {
              const Icon = tool.icon;
              return (
                <button
                  key={tool.capability}
                  type="button"
                  onClick={() => {
                    onOpenChange(false);
                    onSelect(tool.capability);
                  }}
                  className="flex items-start gap-3 p-3 rounded-md border border-border bg-background hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <span className="shrink-0 w-9 h-9 rounded-md bg-accent flex items-center justify-center">
                    <Icon className="h-5 w-5 text-foreground" />
                  </span>
                  <span className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-sm font-medium">{tool.label}</span>
                    <span className="text-xs text-muted-foreground">{tool.description}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
